import { useState } from "react";

import { useT, type Translate } from "../../../shared/i18n";
import { Button, Card, Segmented, Table } from "../../../shared/ui";
import { KeyValueList } from "../KeyValueList";
import { formatDateTime } from "./format";
import type { JobWindow, UserActivity } from "./usersApi";

const WINDOWS: JobWindow[] = ["24h", "7d", "30d"];

function windowLabel(t: Translate, window: JobWindow): string {
  if (window === "24h") return t("admin.users.activity.w24h");
  if (window === "7d") return t("admin.users.activity.w7d");
  return t("admin.users.activity.w30d");
}

function counts(values: Record<string, number>, none: string): string {
  const parts = Object.entries(values).map(
    ([name, count]) => `${name}: ${String(count)}`,
  );
  return parts.length > 0 ? parts.join(", ") : none;
}

/** Job counters per window plus the last jobs, each linking to its logs. */
export function ActivitySection({
  activity,
  onOpenLogs,
}: {
  activity: UserActivity;
  onOpenLogs: (correlationId: string) => void;
}) {
  const t = useT();
  const [window, setWindow] = useState<JobWindow>("24h");
  const jobs = activity.jobs[window];
  const none = t("admin.users.none");
  return (
    <Card title={t("admin.users.activity.title")}>
      <div className="flex flex-col gap-3">
        <Segmented
          label={t("admin.users.activity.window")}
          value={window}
          onChange={(id) => {
            setWindow(id as JobWindow);
          }}
          options={WINDOWS.map((id) => ({ id, label: windowLabel(t, id) }))}
        />
        <KeyValueList
          items={[
            {
              label: t("admin.users.activity.byState"),
              value: counts(jobs.by_state, none),
            },
            {
              label: t("admin.users.activity.byKind"),
              value: counts(jobs.by_request_kind, none),
            },
            {
              label: t("admin.users.activity.sessions"),
              value: String(activity.sessions_count),
            },
            {
              label: t("admin.users.activity.feedbackUp"),
              value: String(activity.feedback.up),
            },
            {
              label: t("admin.users.activity.feedbackDown"),
              value: String(activity.feedback.down),
            },
          ]}
        />
        <Table
          caption={t("admin.users.activity.recent")}
          rows={activity.recent_jobs}
          rowKey={(row) => row.job_id}
          empty={
            <p className="text-body text-fg-muted">
              {t("admin.users.activity.noJobs")}
            </p>
          }
          columns={[
            {
              id: "job",
              header: t("admin.users.job.id"),
              cell: (row) => (
                <span className="font-mono text-footnote">{row.job_id}</span>
              ),
            },
            {
              id: "state",
              header: t("admin.users.job.state"),
              cell: (row) => row.state,
            },
            {
              id: "created",
              header: t("admin.users.job.created"),
              cell: (row) => formatDateTime(row.created_at),
            },
            {
              id: "logs",
              header: t("admin.users.job.logs"),
              cell: (row) => {
                const correlationId = row.correlation_id;
                if (!correlationId) return "-";
                return (
                  <Button
                    variant="tinted"
                    size="sm"
                    onClick={() => {
                      onOpenLogs(correlationId);
                    }}
                  >
                    {t("admin.users.job.openLogs")}
                  </Button>
                );
              },
            },
          ]}
        />
      </div>
    </Card>
  );
}
